import { useRef, useState } from 'react';
import { CameraNaTela } from './Camera';
import { Enquadrar } from './Enquadrar';
import { GABARITO_LIVRE, type Gabarito } from './gabaritos';

/**
 * Tirar a foto na hora — e enquadrar antes de enviar.
 *
 * «para as fotos deverá ter um botão para tirar a foto na hora ou
 *  anexar. Mas a ideia é que a foto seja tirada na hora.»
 *  — Humberto, 02/09/2026.
 *
 * Daí a ordem e o peso dos dois botões: "Tirar foto" é o grande, à
 * direita, onde o polegar cai; "Anexar arquivo" é o discreto. Um
 * `<input type="file">` sozinho — que era o que a tela de adensamento
 * tinha — abre a galeria no celular, e a galeria é o caminho para a foto
 * do ensaio da semana passada entrar no laudo desta.
 *
 * Depois da foto vem SEMPRE o enquadramento (`Enquadrar.tsx`): quadrado,
 * com o gabarito da forma do corpo de prova daquele ensaio. Vários
 * arquivos entram numa fila e são enquadrados um a um — sem fila, o
 * segundo arquivo sobrescreveria o primeiro enquanto a pessoa ainda
 * estava mirando.
 */

const ehTatil = () =>
  typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches;

const temGetUserMedia = () =>
  typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia;

export interface CapturaProps {
  /** O que a foto é — vira o começo do nome do arquivo. */
  tipo: string;
  /** O que a pessoa lê no alto da câmera e do enquadramento. */
  titulo: string;
  gabarito?: Gabarito;
  multiplo?: boolean;
  desabilitado?: boolean;
  /** Recebe cada foto JÁ enquadrada, uma por vez. */
  aoReceber(arquivo: File): void | Promise<void>;
}

export function CapturaDeFoto({
  tipo, titulo, gabarito = GABARITO_LIVRE,
  multiplo = false, desabilitado = false, aoReceber,
}: CapturaProps) {
  const entradaCamera = useRef<HTMLInputElement>(null);
  const entradaArquivo = useRef<HTMLInputElement>(null);
  const [comCamera, setComCamera] = useState(false);
  const [fila, setFila] = useState<File[]>([]);

  const enfileirar = (lista: FileList | File[] | null) => {
    const fs = lista ? Array.from(lista) : [];
    if (!fs.length) return;
    setFila((f) => [...f, ...(multiplo ? fs : fs.slice(0, 1))]);
  };

  const tirar = () => {
    if (desabilitado) return;
    /* No celular, a câmera do próprio aparelho: foco, flash, resolução
       cheia e EXIF — nenhuma câmera desenhada em JavaScript chega perto.
       No computador, `capture` é ignorado e o seletor de pastas apareceria
       no lugar da câmera; aí vale a webcam na sobreposição. */
    if (ehTatil()) entradaCamera.current?.click();
    else if (temGetUserMedia()) setComCamera(true);
    else entradaArquivo.current?.click();
  };

  const atual = fila[0] ?? null;

  return (
    <>
      <div className="captura-botoes">
        <button
          type="button" className="botao secundario" disabled={desabilitado}
          onClick={() => entradaArquivo.current?.click()}
        >
          Anexar arquivo
        </button>
        <button
          type="button" className="botao" disabled={desabilitado}
          onClick={tirar}
        >
          Tirar foto
        </button>
      </div>

      <input
        ref={entradaCamera} type="file" accept="image/*" capture="environment"
        className="só-leitor"
        onChange={(ev) => { enfileirar(ev.target.files); ev.target.value = ''; }}
      />
      <input
        ref={entradaArquivo} type="file" accept="image/*" multiple={multiplo}
        className="só-leitor"
        onChange={(ev) => { enfileirar(ev.target.files); ev.target.value = ''; }}
      />

      {comCamera && (
        <CameraNaTela
          tipo={tipo}
          titulo={titulo}
          aoFechar={() => setComCamera(false)}
          aoPedirArquivo={() => {
            setComCamera(false);
            entradaArquivo.current?.click();
          }}
          aoTirar={(a) => { setComCamera(false); enfileirar([a]); }}
        />
      )}

      {atual && (
        <Enquadrar
          key={`${atual.name}-${atual.size}-${fila.length}`}
          arquivo={atual}
          gabarito={gabarito}
          titulo={`Enquadrar — ${titulo}`}
          aoCancelar={() => setFila((f) => f.slice(1))}
          aoConfirmar={(recortada) => {
            setFila((f) => f.slice(1));
            void aoReceber(recortada);
          }}
        />
      )}
    </>
  );
}
